// ** UI
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';

const STAGGER_COUNT = 12;
const STAGGER_STEP_MS = 80;

const staggerDelays = Object.fromEntries(
	Array.from({ length: STAGGER_COUNT }, (_, i) => [
		`& > *:nth-of-type(${i + 1})`,
		{ animationDelay: `${(i + 1) * STAGGER_STEP_MS}ms` }
	])
) as Record<string, { animationDelay: string }>;

export type NewsGridProps = {
	children: React.ReactNode;
	sx?: SxProps<Theme>;
};

const NewsGrid = ({ children, sx }: NewsGridProps) => {
	const theme = useTheme();
	const sp = theme.spacing;

	return (
		<Box
			sx={[
				{
					display: 'flex',
					flexWrap: 'wrap',
					justifyContent: { xs: 'center', sm: 'flex-start' },
					gap: { xs: sp(6), md: sp(4) },
					alignItems: 'stretch',
					'@keyframes newsGridFadeUp': {
						from: {
							opacity: 0,
							transform: 'translateY(12px)'
						},
						to: {
							opacity: 1,
							transform: 'translateY(0)'
						}
					},
					'& > *': {
						minWidth: 0,
						opacity: 0,
						animationName: 'newsGridFadeUp',
						animationDuration: '800ms',
						animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
						animationFillMode: 'forwards',
						// mobile: 1 per row
						flex: '0 1 100%',
						// tablet: 2 per row
						'@media (min-width: 600px)': {
							flex: `0 1 calc((100% - ${sp(6)}) / 2)`,
							maxWidth: 'none !important'
						},
						// desktop: 3 per row
						'@media (min-width: 960px)': {
							flex: `0 1 calc((100% - ${sp(4)} - ${sp(4)}) / 3)`
						},
						'@media (prefers-reduced-motion: reduce)': {
							opacity: 1,
							animation: 'none'
						}
					},
					...staggerDelays
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			{children}
		</Box>
	);
};

export default NewsGrid;
